/**
 * KK-602 / KK-610 / KK-514 · Tedarikçi cari hesabı.
 *
 * Ekstrede borç ve alacak AYRI sütunlardır; tek işaretli sütun muhasebeci
 * gözüyle okunamaz. Yürüyen bakiye SUNUCUDA hesaplanır — istemcide
 * hesaplansaydı sayfalama yapıldığında ekrandaki bakiye ile gerçek ayrışırdı.
 *
 * Mahsup varsayımı FIFO'dur: ödemeler en eski faturadan başlayarak kapatır.
 * KK-610 iade mahsubu, "iade alacağı" tipindeki hareketle yazılır ve bir
 * sonraki faturayla mahsuplaşır.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  Buton,
  Kart,
  Rozet,
  Secim,
  Tablo,
  TabloBaslik,
  TabloGovde,
  TelefonAlani,
  Tutar,
  Uyari,
} from "@kantin/ui";
import { IstatistikKarosu } from "@kantin/ui/grafik";
import { telefonGoster } from "@kantin/shared";
import { apiOku, apiYaz } from "./api.js";
import { noktaSecenekleri, useNoktalar, useTedarikciler, useUrunler } from "./kancalar.js";
import { UrunSecimi } from "./UrunSecimi.js";
import {
  AramaKutusu,
  IslemDurumu,
  MetinAlani,
  Sayfa,
  SayfaBasligi,
  Sekmeler,
  SorguDurumu,
  ayBasi,
  bugun,
  miktarGecerliMi,
  miktarGoster,
  miktarNormalize,
  paraGecerliMi,
  paraNormalize,
  tarihGoster,
} from "./ortak.js";

// ---------------------------------------------------------------------------

const CariHareketTipi = { FATURA: 1, ODEME: 2, IADE_ALACAGI: 3 } as const;

const CARI_TIP_ADI: Record<number, string> = {
  [CariHareketTipi.FATURA]: "Fatura (borç)",
  [CariHareketTipi.ODEME]: "Ödeme",
  [CariHareketTipi.IADE_ALACAGI]: "İade alacağı (mahsup)",
};

interface CariHareket {
  id: string;
  tip: number;
  tip_ad: string;
  borc: string;
  alacak: string;
  tutar: string;
  belge_no: string | null;
  vade: string | null;
  aciklama: string | null;
  olusma_zamani: string;
  yuruyen_bakiye?: string;
}

interface EkstreYaniti {
  tedarikci: { id: string; ad: string };
  bas: string;
  bit: string;
  devir_bakiye: string;
  hareketler: CariHareket[];
  kapanis_bakiye: string;
  vade: {
    fatura_toplami: string;
    odeme_toplami: string;
    vadesi_gecmis_acik_tutar: string;
    en_eski_gecikmis_vade: string | null;
    mahsup_varsayimi: string;
  };
}

type Sekme = "liste" | "ekstre" | "fatura";

export function Cari() {
  const [sekme, setSekme] = useState<Sekme>("liste");
  const [seciliTedarikci, setSeciliTedarikci] = useState<string>("");

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Cari hesaplar"
        aciklama="Tedarikçi listesi, cari ekstre, fatura/ödeme kaydı ve iade mahsubu. Mal girişi olan fatura ayrı bir ekrandan girilir."
      />
      <Sekmeler<Sekme>
        etiket="Cari bölümleri"
        secili={sekme}
        sec={setSekme}
        sekmeler={[
          { deger: "liste", metin: "Tedarikçiler" },
          { deger: "ekstre", metin: "Cari ekstre" },
          { deger: "fatura", metin: "Alım faturası (mal girişi)" },
        ]}
      />

      {sekme === "liste" && (
        <TedarikciListesi
          ekstreyeGit={(id) => {
            setSeciliTedarikci(id);
            setSekme("ekstre");
          }}
        />
      )}
      {sekme === "ekstre" && (
        <CariEkstre
          tedarikciId={seciliTedarikci}
          secildi={setSeciliTedarikci}
        />
      )}
      {sekme === "fatura" && <AlimFaturasi />}
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------

function TedarikciListesi({
  ekstreyeGit,
}: {
  ekstreyeGit: (id: string) => void;
}) {
  const [arama, setArama] = useState("");
  const [yeniAcik, setYeniAcik] = useState(false);
  const sorgu = useTedarikciler(arama);
  const tedarikciler = sorgu.data?.tedarikciler ?? [];

  return (
    <>
      <Kart
        baslik="Arama"
        ustBilgi={
          <Buton
            tur="birincil"
            onClick={() => setYeniAcik((a) => !a)}
            aria-expanded={yeniAcik}
          >
            {yeniAcik ? "Formu kapat" : "Yeni tedarikçi"}
          </Buton>
        }
      >
        <div className="grid gap-3 md:grid-cols-3">
          <AramaKutusu
            etiket="Tedarikçi adı"
            deger={arama}
            degistir={setArama}
          />
        </div>
      </Kart>

      {yeniAcik && (
        <YeniTedarikci
          kapat={() => setYeniAcik(false)}
          kaydedildi={() => {
            setYeniAcik(false);
            void sorgu.refetch();
          }}
        />
      )}

      <Kart
        baslik="Tedarikçiler"
        ustBilgi={
          <span className="text-sm text-murekkep-2">
            Toplam borç:{" "}
            <Tutar
              deger={sorgu.data?.toplam_borc ?? "0.00"}
              yonsuz
              etiket="toplam borç"
            />
          </span>
        }
      >
        <SorguDurumu
          yukleniyor={sorgu.isPending}
          hata={sorgu.error}
          bos={tedarikciler.length === 0}
          bosBaslik="Tedarikçi yok"
          bosAciklama="Mal girişi yapabilmek için önce tedarikçi tanımlayın."
          yenile={() => void sorgu.refetch()}
        >
          <Tablo>
            <TabloBaslik>
              <th scope="col">Tedarikçi</th>
              <th scope="col">Bakiye (borç)</th>
              <th scope="col">Vadesi geçmiş</th>
              <th scope="col">En yakın vade</th>
              <th scope="col">
                <span className="gizli-erisilebilir">İşlem</span>
              </th>
            </TabloBaslik>
            <TabloGovde>
              {tedarikciler.map((t) => (
                <tr key={t.id}>
                  <td>
                    <p className="m-0 font-semibold text-murekkep">{t.ad}</p>
                    <p className="m-0 text-xs text-murekkep-3">
                      {t.vergi_no ? `VKN ${t.vergi_no}` : "VKN yok"}
                      {t.telefon ? ` · ${telefonGoster(t.telefon)}` : ""}
                      {!t.aktif ? " · pasif" : ""}
                    </p>
                  </td>
                  <td className="text-right">
                    <Tutar deger={t.bakiye} yonsuz etiket="cari bakiye" />
                  </td>
                  <td className="text-right">
                    {t.gecikmis_tutar === "0.00" ? (
                      <span className="text-xs text-murekkep-3">—</span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <Tutar
                          deger={t.gecikmis_tutar}
                          yonsuz
                          etiket="vadesi geçmiş tutar"
                        />
                        <Rozet tur="hata">gecikmiş</Rozet>
                      </span>
                    )}
                  </td>
                  <td className="text-sm text-murekkep-2">
                    {tarihGoster(t.en_yakin_vade)}
                  </td>
                  <td className="text-right">
                    <Buton tur="sessiz" onClick={() => ekstreyeGit(t.id)}>
                      Ekstre
                    </Buton>
                  </td>
                </tr>
              ))}
            </TabloGovde>
          </Tablo>
        </SorguDurumu>
      </Kart>
    </>
  );
}

function YeniTedarikci({
  kapat,
  kaydedildi,
}: {
  kapat: () => void;
  kaydedildi: () => void;
}) {
  const [ad, setAd] = useState("");
  const [vergiNo, setVergiNo] = useState("");
  const [telefon, setTelefon] = useState("");
  const [eposta, setEposta] = useState("");
  const [adres, setAdres] = useState("");
  const [gonderildi, setGonderildi] = useState(false);

  const kaydet = useMutation({
    mutationFn: () =>
      apiYaz<{ id: string }>("POST", "/tedarikci", {
        ad: ad.trim(),
        vergi_no: vergiNo.trim() || null,
        telefon: telefon.trim() || null,
        eposta: eposta.trim() || null,
        adres: adres.trim() || null,
        aktif: true,
      }),
    onSuccess: kaydedildi,
  });

  const adHatasi =
    gonderildi && !ad.trim() ? "Tedarikçi adı zorunludur." : undefined;

  return (
    <Kart baslik="Yeni tedarikçi">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setGonderildi(true);
          if (!ad.trim()) return;
          kaydet.mutate();
        }}
      >
        <div className="grid gap-3 md:grid-cols-2">
          <Alan
            etiket="Tedarikçi adı"
            value={ad}
            onChange={(e) => setAd(e.target.value)}
            {...(adHatasi ? { hata: adHatasi } : {})}
          />
          <Alan
            etiket="Vergi numarası"
            value={vergiNo}
            onChange={(e) => setVergiNo(e.target.value)}
            inputMode="numeric"
          />
          <TelefonAlani
            etiket="Telefon"
            value={telefon}
            onChange={setTelefon}
          />
          <Alan
            etiket="E-posta"
            type="email"
            value={eposta}
            onChange={(e) => setEposta(e.target.value)}
          />
        </div>
        <MetinAlani
          etiket="Adres"
          value={adres}
          onChange={(e) => setAdres(e.target.value)}
          rows={2}
        />
        <IslemDurumu hata={kaydet.error} />
        <div className="flex flex-wrap gap-2">
          <Buton type="submit" tur="birincil" yukleniyor={kaydet.isPending}>
            Kaydet
          </Buton>
          <Buton type="button" tur="ikincil" onClick={kapat}>
            Vazgeç
          </Buton>
        </div>
      </form>
    </Kart>
  );
}

// ---------------------------------------------------------------------------

function CariEkstre({
  tedarikciId,
  secildi,
}: {
  tedarikciId: string;
  secildi: (id: string) => void;
}) {
  const [bas, setBas] = useState(ayBasi());
  const [bit, setBit] = useState(bugun());
  const tedarikciler = useTedarikciler();

  const sorgu = useQuery({
    queryKey: ["tedarikci-ekstre", tedarikciId, bas, bit],
    queryFn: () =>
      apiOku<EkstreYaniti>(`/tedarikci/${tedarikciId}/ekstre`, { bas, bit }),
    enabled: tedarikciId !== "",
  });

  return (
    <>
      <Kart baslik="Ekstre seçimi">
        <div className="grid gap-3 md:grid-cols-3">
          <Secim
            etiket="Tedarikçi"
            value={tedarikciId}
            onChange={(e) => secildi(e.target.value)}
            secenekler={[
              { deger: "", metin: "Seçiniz" },
              ...(tedarikciler.data?.tedarikciler ?? []).map((t) => ({
                deger: t.id,
                metin: t.ad,
              })),
            ]}
          />
          <Alan
            etiket="Başlangıç"
            type="date"
            value={bas}
            onChange={(e) => setBas(e.target.value)}
          />
          <Alan
            etiket="Bitiş"
            type="date"
            value={bit}
            onChange={(e) => setBit(e.target.value)}
          />
        </div>
      </Kart>

      {tedarikciId === "" ? (
        <Kart baslik="Ekstre">
          <p className="m-0 text-sm text-murekkep-3">
            Ekstreyi görmek için bir tedarikçi seçin.
          </p>
        </Kart>
      ) : (
        <>
          <SorguDurumu
            yukleniyor={sorgu.isPending}
            hata={sorgu.error}
            bos={!sorgu.data}
            bosBaslik="Ekstre bulunamadı"
            yenile={() => void sorgu.refetch()}
          >
            {sorgu.data && (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <IstatistikKarosu
                    etiket="Devir bakiye"
                    deger={
                      <Tutar
                        deger={sorgu.data.devir_bakiye}
                        yonsuz
                        boy="2xl"
                        etiket="devir bakiye"
                      />
                    }
                  />
                  <IstatistikKarosu
                    etiket="Dönem sonu bakiye"
                    deger={
                      <Tutar
                        deger={sorgu.data.kapanis_bakiye}
                        yonsuz
                        boy="2xl"
                        etiket="kapanış bakiye"
                      />
                    }
                  />
                  <IstatistikKarosu
                    etiket="Vadesi geçmiş açık tutar"
                    deger={
                      <Tutar
                        deger={sorgu.data.vade.vadesi_gecmis_acik_tutar}
                        yonsuz
                        boy="2xl"
                        etiket="vadesi geçmiş açık tutar"
                      />
                    }
                    yardimci={
                      sorgu.data.vade.en_eski_gecikmis_vade
                        ? `En eski vade: ${tarihGoster(sorgu.data.vade.en_eski_gecikmis_vade)}`
                        : "Gecikmiş vade yok"
                    }
                    vurgu={sorgu.data.vade.vadesi_gecmis_acik_tutar !== "0.00"}
                  />
                  <IstatistikKarosu
                    etiket="Ödeme toplamı"
                    deger={
                      <Tutar
                        deger={sorgu.data.vade.odeme_toplami}
                        yonsuz
                        boy="2xl"
                        etiket="ödeme toplamı"
                      />
                    }
                    yardimci={sorgu.data.vade.mahsup_varsayimi}
                  />
                </div>

                <Kart baslik="Hareketler">
                  <SorguDurumu
                    yukleniyor={false}
                    hata={null}
                    bos={sorgu.data.hareketler.length === 0}
                    bosBaslik="Bu dönemde hareket yok"
                    bosAciklama="Tarih aralığını genişletin."
                  >
                    <Tablo>
                      <TabloBaslik>
                        <th scope="col">Tarih</th>
                        <th scope="col">İşlem</th>
                        <th scope="col">Belge</th>
                        <th scope="col">Vade</th>
                        <th scope="col">Borç</th>
                        <th scope="col">Alacak</th>
                        <th scope="col">Yürüyen bakiye</th>
                      </TabloBaslik>
                      <TabloGovde>
                        {sorgu.data.hareketler.map((h) => (
                          <tr key={h.id}>
                            <td className="whitespace-nowrap text-sm">
                              {tarihGoster(h.olusma_zamani)}
                            </td>
                            <td className="text-sm text-murekkep-2">
                              {h.tip_ad}
                              {h.aciklama && (
                                <span className="block text-xs text-murekkep-3">
                                  {h.aciklama}
                                </span>
                              )}
                            </td>
                            <td className="rakam font-mono text-xs">
                              {h.belge_no ?? "—"}
                            </td>
                            <td className="text-sm text-murekkep-2">
                              {tarihGoster(h.vade)}
                            </td>
                            <td className="text-right">
                              {h.borc === "0.00" ? (
                                <span className="text-murekkep-3">—</span>
                              ) : (
                                <Tutar deger={h.borc} yonsuz boy="sm" />
                              )}
                            </td>
                            <td className="text-right">
                              {h.alacak === "0.00" ? (
                                <span className="text-murekkep-3">—</span>
                              ) : (
                                <Tutar deger={h.alacak} yonsuz boy="sm" />
                              )}
                            </td>
                            <td className="text-right">
                              <Tutar
                                deger={h.yuruyen_bakiye ?? "0.00"}
                                yonsuz
                                boy="sm"
                                etiket="yürüyen bakiye"
                              />
                            </td>
                          </tr>
                        ))}
                      </TabloGovde>
                    </Tablo>
                  </SorguDurumu>
                </Kart>
              </>
            )}
          </SorguDurumu>

          <HareketFormu tedarikciId={tedarikciId} />
        </>
      )}
    </>
  );
}

function HareketFormu({ tedarikciId }: { tedarikciId: string }) {
  const istemci = useQueryClient();
  const [tip, setTip] = useState(String(CariHareketTipi.ODEME));
  const [tutar, setTutar] = useState("");
  const [belgeNo, setBelgeNo] = useState("");
  const [vade, setVade] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [gonderildi, setGonderildi] = useState(false);

  const kaydet = useMutation({
    mutationFn: () =>
      apiYaz<{ id: string; bakiye: string }>(
        "POST",
        `/tedarikci/${tedarikciId}/hareket`,
        {
          tip: Number.parseInt(tip, 10),
          tutar: paraNormalize(tutar),
          belge_no: belgeNo.trim() || null,
          vade: vade || null,
          aciklama: aciklama.trim() || null,
        },
      ),
    onSuccess: () => {
      setTutar("");
      setBelgeNo("");
      setVade("");
      setAciklama("");
      setGonderildi(false);
      void istemci.invalidateQueries({ queryKey: ["tedarikci"] });
      void istemci.invalidateQueries({ queryKey: ["tedarikci-ekstre"] });
    },
  });

  const tutarHatasi =
    gonderildi && !paraGecerliMi(tutar)
      ? "Tutarı 1250,00 biçiminde ve sıfırdan büyük girin."
      : undefined;
  const iadeMi = Number.parseInt(tip, 10) === CariHareketTipi.IADE_ALACAGI;

  return (
    <Kart baslik="Fatura / ödeme / iade mahsubu">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setGonderildi(true);
          if (!paraGecerliMi(tutar)) return;
          kaydet.mutate();
        }}
      >
        <Uyari tur="bilgi" baslik="Bu form STOK ÜRETMEZ">
          Hizmet faturası, ödeme ve iade mahsubu içindir. Mal girişi olan fatura{" "}
          <strong>Alım faturası</strong> sekmesinden girilir; orada fatura, stok
          ve cari borç tek işlemde yazılır.
        </Uyari>

        <div className="grid gap-3 md:grid-cols-4">
          <Secim
            etiket="Hareket tipi"
            value={tip}
            onChange={(e) => setTip(e.target.value)}
            secenekler={Object.entries(CARI_TIP_ADI).map(([k, v]) => ({
              deger: k,
              metin: v,
            }))}
          />
          <Alan
            etiket="Tutar"
            value={tutar}
            onChange={(e) => setTutar(e.target.value)}
            inputMode="decimal"
            ipucu="Tutar her zaman pozitif girilir; yönü hareket tipi belirler."
            {...(tutarHatasi ? { hata: tutarHatasi } : {})}
          />
          <Alan
            etiket="Belge no"
            value={belgeNo}
            onChange={(e) => setBelgeNo(e.target.value)}
          />
          <Alan
            etiket="Vade"
            type="date"
            value={vade}
            onChange={(e) => setVade(e.target.value)}
          />
        </div>

        {iadeMi && (
          <Uyari tur="uyari" baslik="İade mahsubu (KK-610)">
            Tedarikçiye iade edilen mal için alacak kaydı düşer ve bir sonraki
            fatura ile mahsuplaşır. Mahsup varsayımı FIFO'dur.
          </Uyari>
        )}

        <MetinAlani
          etiket="Açıklama"
          value={aciklama}
          onChange={(e) => setAciklama(e.target.value)}
          rows={2}
        />

        <IslemDurumu hata={kaydet.error} />
        {kaydet.data && (
          <Uyari tur="onay" baslik="Hareket kaydedildi">
            Yeni cari bakiye:{" "}
            <Tutar
              deger={kaydet.data.bakiye}
              yonsuz
              boy="sm"
              etiket="yeni cari bakiye"
            />
          </Uyari>
        )}

        <div>
          <Buton type="submit" tur="birincil" yukleniyor={kaydet.isPending}>
            Hareketi kaydet
          </Buton>
        </div>
      </form>
    </Kart>
  );
}

// ---------------------------------------------------------------------------
// KK-514 · Alım faturası (fatura + stok girişi + cari borç, TEK işlem)
// ---------------------------------------------------------------------------

interface FaturaSatiri {
  urunId: string;
  miktar: string;
  birimAlis: string;
  sonKullanma: string;
  partiKodu: string;
}

const BOS_SATIR: FaturaSatiri = {
  urunId: "",
  miktar: "",
  birimAlis: "",
  sonKullanma: "",
  partiKodu: "",
};

function AlimFaturasi() {
  const istemci = useQueryClient();
  const tedarikciler = useTedarikciler();
  const noktalar = useNoktalar({ aktif: true });

  const [tedarikciId, setTedarikciId] = useState("");
  const [hedefNokta, setHedefNokta] = useState("");
  const [belgeNo, setBelgeNo] = useState("");
  const [faturaTarihi, setFaturaTarihi] = useState(bugun());
  const [vade, setVade] = useState("");
  const [satirlar, setSatirlar] = useState<FaturaSatiri[]>([{ ...BOS_SATIR }]);
  const [gonderildi, setGonderildi] = useState(false);

  const urunler = useUrunler({ aktif: true });

  const urunHarita = new Map(
    (urunler.data?.urunler ?? []).map((u) => [u.id, u]),
  );

  const kaydet = useMutation({
    mutationFn: () =>
      apiYaz<{
        fatura_id: string;
        toplam: string;
        hedef_nokta_id: string;
        satir_sayisi: number;
      }>("POST", "/alim-faturasi", {
        tedarikci_id: tedarikciId,
        hedef_nokta_id: hedefNokta || null,
        belge_no: belgeNo.trim() || null,
        fatura_tarihi: faturaTarihi,
        vade: vade || null,
        satirlar: satirlar
          .filter((s) => s.urunId && s.miktar && s.birimAlis)
          .map((s) => ({
            urun_id: s.urunId,
            miktar: miktarNormalize(s.miktar),
            birim_alis: paraNormalize(s.birimAlis),
            son_kullanma: s.sonKullanma || null,
            parti_kodu: s.partiKodu.trim() || null,
          })),
      }),
    onSuccess: () => {
      setSatirlar([{ ...BOS_SATIR }]);
      setBelgeNo("");
      setGonderildi(false);
      void istemci.invalidateQueries({ queryKey: ["tedarikci"] });
      void istemci.invalidateQueries({ queryKey: ["stok"] });
    },
  });

  const gecerliSatirlar = satirlar.filter(
    (s) => s.urunId && miktarGecerliMi(s.miktar) && paraGecerliMi(s.birimAlis),
  );
  const sktEksikVar = satirlar.some(
    (s) => s.urunId && urunHarita.get(s.urunId)?.skt_takibi && !s.sonKullanma,
  );

  return (
    <Kart baslik="Alım faturası">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setGonderildi(true);
          if (!tedarikciId || gecerliSatirlar.length === 0 || sktEksikVar)
            return;
          kaydet.mutate();
        }}
      >
        <Uyari
          tur="bilgi"
          baslik="Fatura, stok ve cari borç TEK işlemde yazılır"
        >
          Hedef nokta boş bırakılırsa mal <strong>depoya</strong> girer.
          Noktalara dağıtım transferle yapılır.
        </Uyari>

        <div className="grid gap-3 md:grid-cols-4">
          <Secim
            etiket="Tedarikçi"
            value={tedarikciId}
            onChange={(e) => setTedarikciId(e.target.value)}
            secenekler={[
              { deger: "", metin: "Seçiniz" },
              ...(tedarikciler.data?.tedarikciler ?? []).map((t) => ({
                deger: t.id,
                metin: t.ad,
              })),
            ]}
          />
          <Secim
            etiket="Hedef nokta"
            value={hedefNokta}
            onChange={(e) => setHedefNokta(e.target.value)}
            secenekler={noktaSecenekleri(
              noktalar.data?.noktalar,
              "Okul deposu (varsayılan)",
            )}
          />
          <Alan
            etiket="Belge no"
            value={belgeNo}
            onChange={(e) => setBelgeNo(e.target.value)}
          />
          <Alan
            etiket="Fatura tarihi"
            type="date"
            value={faturaTarihi}
            onChange={(e) => setFaturaTarihi(e.target.value)}
          />
          <Alan
            etiket="Vade"
            type="date"
            value={vade}
            onChange={(e) => setVade(e.target.value)}
          />
        </div>

        {gonderildi && !tedarikciId && (
          <p className="m-0 text-xs text-hata" role="alert">
            Tedarikçi seçin.
          </p>
        )}

        <section aria-label="Fatura satırları">
          <h3 className="m-0 mb-2 text-lg">Satırlar</h3>
          <IslemDurumu hata={urunler.error} />
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {satirlar.map((s, i) => {
              const urun = s.urunId ? urunHarita.get(s.urunId) : undefined;
              const sktGerekli = urun?.skt_takibi ?? false;
              return (
                <li
                  key={i}
                  className="grid gap-2 rounded-lg border border-cetvel p-3 md:grid-cols-5"
                >
                  <UrunSecimi
                    etiket={`Satır ${i + 1} ürün`}
                    deger={s.urunId}
                    degistir={(urunId) =>
                      setSatirlar((o) =>
                        o.map((x, j) =>
                          j === i ? { ...x, urunId } : x,
                        ),
                      )
                    }
                    urunler={urunler.data?.urunler}
                    pasif={urunler.isPending || urunler.isError}
                  />
                  <Alan
                    etiket="Miktar"
                    value={s.miktar}
                    onChange={(e) =>
                      setSatirlar((o) =>
                        o.map((x, j) =>
                          j === i ? { ...x, miktar: e.target.value } : x,
                        ),
                      )
                    }
                    inputMode="decimal"
                    {...(gonderildi && s.urunId && !miktarGecerliMi(s.miktar)
                      ? { hata: "Miktarı 10 veya 10,500 biçiminde girin." }
                      : {})}
                  />
                  <Alan
                    etiket="Birim alış fiyatı"
                    value={s.birimAlis}
                    onChange={(e) =>
                      setSatirlar((o) =>
                        o.map((x, j) =>
                          j === i ? { ...x, birimAlis: e.target.value } : x,
                        ),
                      )
                    }
                    inputMode="decimal"
                    {...(gonderildi && s.urunId && !paraGecerliMi(s.birimAlis)
                      ? { hata: "Fiyatı 12,50 biçiminde girin." }
                      : {})}
                  />
                  <Alan
                    etiket={
                      sktGerekli ? "Son kullanma (zorunlu)" : "Son kullanma"
                    }
                    type="date"
                    value={s.sonKullanma}
                    onChange={(e) =>
                      setSatirlar((o) =>
                        o.map((x, j) =>
                          j === i ? { ...x, sonKullanma: e.target.value } : x,
                        ),
                      )
                    }
                    {...(sktGerekli && !s.sonKullanma && gonderildi
                      ? {
                          hata: "Bu üründe SKT takibi açık; tarih zorunludur.",
                        }
                      : {})}
                  />
                  <div className="flex flex-col gap-2">
                    <Alan
                      etiket="Parti kodu"
                      value={s.partiKodu}
                      onChange={(e) =>
                        setSatirlar((o) =>
                          o.map((x, j) =>
                            j === i ? { ...x, partiKodu: e.target.value } : x,
                          ),
                        )
                      }
                    />
                    <Buton
                      type="button"
                      tur="sessiz"
                      onClick={() =>
                        setSatirlar((o) =>
                          o.length === 1
                            ? [{ ...BOS_SATIR }]
                            : o.filter((_, j) => j !== i),
                        )
                      }
                      aria-label={`Satır ${i + 1} kaldır`}
                    >
                      Satırı kaldır
                    </Buton>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="mt-2">
            <Buton
              type="button"
              tur="ikincil"
              onClick={() => setSatirlar((o) => [...o, { ...BOS_SATIR }])}
            >
              Satır ekle
            </Buton>
          </div>
          {gonderildi && gecerliSatirlar.length === 0 && (
            <p className="m-0 mt-2 text-xs text-hata" role="alert">
              En az bir geçerli satır girin.
            </p>
          )}
        </section>

        <IslemDurumu hata={kaydet.error} />
        {kaydet.data && (
          <Uyari tur="onay" baslik="Fatura kaydedildi">
            <span className="rakam font-mono">{kaydet.data.satir_sayisi}</span>{" "}
            satır · Toplam:{" "}
            <Tutar
              deger={kaydet.data.toplam}
              yonsuz
              boy="sm"
              etiket="fatura toplamı"
            />{" "}
            · Stok girişi ve cari borç aynı işlemde yazıldı.
          </Uyari>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Buton type="submit" tur="birincil" yukleniyor={kaydet.isPending}>
            Faturayı kaydet
          </Buton>
          <span className="text-sm text-murekkep-3">
            Toplam{" "}
            <span className="rakam font-mono">{gecerliSatirlar.length}</span>{" "}
            geçerli satır
            {gecerliSatirlar.length > 0 && (
              <>
                {" "}
                ·{" "}
                {gecerliSatirlar
                  .map((s) => miktarGoster(miktarNormalize(s.miktar)))
                  .join(" + ")}
              </>
            )}
          </span>
        </div>
      </form>
    </Kart>
  );
}
